import { API_BASE, apiFetch } from "../app/api/apiBase";
import { fetchAuthSession, RateLimitError, startEmailMagicLink, startGoogleAuth } from "../app/api/authApi";
import { readLastMagicLinkEmail, writeLastMagicLinkEmail } from "../app/auth/lastMagicLinkEmailStorage";
import {
  faqItems,
} from "../app/landing/landingContent";
import pricingTiersData from "../app/landing/pricingTiers.json";
import {
  applyHomeGeoSeo,
  applyPricingGeoSeo,
} from "./geoSeo";
import { LEGAL_DOCUMENTS, PUBLIC_DOC_PATHS, type LegalDocKey } from "./legalDocuments";
import {
  isSecurityPath,
  setupSecurityPage,
  showSecurityPage,
} from "./securityPage";
import {
  isChangelogPath,
  setupChangelogPage,
  showChangelogPage,
} from "./changelogPage";
import {
  isToolkitPath,
  setupToolkitPage,
  showToolkitPage,
} from "./toolkitPage";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAGIC_LINK_COOLDOWN_MS = 2 * 60 * 1000; // 2 minutes
const MAGIC_LINK_COOLDOWN_KEY = "emailperfect:magic_link_cooldown";

function readMagicLinkCooldown(): { email: string; sentAt: number } | null {
  try {
    const raw = localStorage.getItem(MAGIC_LINK_COOLDOWN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as Record<string, unknown>).email !== "string" ||
      typeof (parsed as Record<string, unknown>).sentAt !== "number"
    ) {
      return null;
    }
    return parsed as { email: string; sentAt: number };
  } catch {
    return null;
  }
}

function writeMagicLinkCooldown(email: string): void {
  try {
    localStorage.setItem(MAGIC_LINK_COOLDOWN_KEY, JSON.stringify({ email: email.toLowerCase(), sentAt: Date.now() }));
  } catch {
    // Private mode or storage unavailable
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function renderLegalBody(doc: LegalDocKey): string {
  const d = LEGAL_DOCUMENTS[doc];
  let html = `<p class="legal-doc-intro">${escapeHtml(d.intro)}</p>`;
  for (const section of d.sections) {
    html += `<section class="legal-doc-section"><h3 class="legal-doc-heading">${escapeHtml(section.heading)}</h3>`;
    for (const p of section.paragraphs) {
      html += `<p class="legal-doc-paragraph">${escapeHtml(p)}</p>`;
    }
    for (const p of section.htmlParagraphs ?? []) {
      html += `<p class="legal-doc-paragraph">${p}</p>`;
    }
    if (section.bullets?.length) {
      html += `<ul class="legal-doc-list">`;
      for (const b of section.bullets) {
        html += `<li>${escapeHtml(b)}</li>`;
      }
      html += `</ul>`;
    }
    html += `</section>`;
  }
  return html;
}

function redirectLegacyLegalPaths(): void {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  if (path === "/cookies") {
    window.history.replaceState({}, "", "/security/cookies");
  }
}

function publicDocKeyFromPath(path: string): LegalDocKey | null {
  return PUBLIC_DOC_PATHS[path] ?? null;
}

function injectFaq(leftId = "faq-col-left", rightId = "faq-col-right"): void {
  const split = Math.ceil(faqItems.length / 2);
  const left = faqItems.slice(0, split);
  const right = faqItems.slice(split);
  const col = (items: typeof faqItems) =>
    items
      .map(
        (item) =>
          `<details><summary>${escapeHtml(item.question)}</summary><p>${escapeHtml(item.answer)}</p></details>`,
      )
      .join("");

  const fq = document.getElementById(leftId);
  const fr = document.getElementById(rightId);
  if (fq) fq.innerHTML = col(left);
  if (fr) fr.innerHTML = col(right);
}

type PricingTierRow = (typeof pricingTiersData.tiers)[number];
type PricingFeatureRow = (typeof pricingTiersData.features)[number];

type PricingTierCardData = PricingTierRow & {
  cardFeaturesIntro?: string;
  cardHighlights?: string[];
};

type PricingFeatureGroup = {
  label: string;
  features: string[];
};

type PricingBillingPeriod = "monthly" | "yearly";

const PRICING_CARD_TIER_IDS = ["free", "core", "pro"] as const;

const PRICING_ENTERPRISE_BUILDING_ICON = `<svg class="pricing-enterprise-callout-icon" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 22V6l6-3 6 3v16"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/><path d="M6 22h12"/></svg>`;

function isPricingPath(): boolean {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  return path === "/pricing";
}

function formatPricingFeatureValue(value: string): string {
  const formatted = value === "No" ? "—" : value.replace(/^No /, "— ");

  return formatted
    .split(/\b(Unlimited)\b/g)
    .map((part) =>
      part === "Unlimited"
        ? '<span class="pricing-infinity" aria-label="Unlimited">∞</span>'
        : escapeHtml(part),
    )
    .join("");
}

function renderPricingTableCell(raw: string): string {
  const value = String(raw).trim();

  if (value === "Yes") {
    return `<span class="pricing-table-indicator is-included is-check" aria-label="Included"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="currentColor"/><path d="M7.5 12.2 10.35 15.05 16.5 8.9" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`;
  }

  if (value === "No" || value === "—" || value.startsWith("No ")) {
    return `<span class="pricing-table-indicator is-unavailable" aria-label="Not included"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="currentColor"/><path d="M8 12h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></span>`;
  }

  if (/\bUnlimited\b/i.test(value)) {
    return `<span class="pricing-table-indicator is-included is-infinity" aria-label="Unlimited"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="12" fill="currentColor"/><text x="12" y="16.5" text-anchor="middle" fill="#fff" font-size="13" font-weight="600" font-family="inherit">∞</text></svg></span>`;
  }

  return `<span class="pricing-table-value-text">${formatPricingFeatureValue(value)}</span>`;
}

function renderPricingFeatureGroupRows(featureLabels: string[]): string {
  const featureByLabel = new Map(pricingTiersData.features.map((feature) => [feature.label, feature]));

  return featureLabels
    .map((label) => {
      const feature = featureByLabel.get(label);
      if (!feature) return "";

      const cells = pricingTiersData.tiers
        .map((tier) => {
          const value = feature.values[tier.id as keyof PricingFeatureRow["values"]] ?? "—";
          return `<td class="pricing-table-feature-value">${renderPricingTableCell(String(value))}</td>`;
        })
        .join("");

      return `<tr class="pricing-feature-row"><th scope="row" class="pricing-table-feature-label">${escapeHtml(feature.label)}</th>${cells}</tr>`;
    })
    .join("");
}

function renderPricingFeatureGroups(): string {
  const groups = (pricingTiersData as typeof pricingTiersData & { featureGroups: PricingFeatureGroup[] })
    .featureGroups;

  return groups
    .map(
      (group: PricingFeatureGroup) => `<tbody class="pricing-feature-group">
    <tr class="pricing-feature-group-header-row">
      <th colspan="${pricingTiersData.tiers.length + 1}" scope="colgroup">
        <button type="button" class="pricing-feature-group-toggle" aria-expanded="true">
          <svg class="pricing-feature-group-chevron" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
          <span>${escapeHtml(group.label)}</span>
        </button>
      </th>
    </tr>
    ${renderPricingFeatureGroupRows(group.features)}
  </tbody>`,
    )
    .join("");
}

function setupPricingCompareGroups(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>(".pricing-feature-group-toggle").forEach((button) => {
    button.addEventListener("click", () => {
      const group = button.closest(".pricing-feature-group");
      if (!group) return;

      const collapsed = group.classList.toggle("is-collapsed");
      button.setAttribute("aria-expanded", collapsed ? "false" : "true");
    });
  });
}

function renderPricingTierCta(tier: PricingTierRow): string {
  const className = tier.id === "free" ? "pricing-tier-cta" : "pricing-tier-cta is-secondary";
  if (tier.cta.type === "auth") {
    return `<button type="button" class="${className}" data-open-auth>${escapeHtml(tier.cta.label)}</button>`;
  }
  return `<a class="${className}" href="${escapeHtml(tier.cta.href ?? "")}">${escapeHtml(tier.cta.label)}</a>`;
}

const PRICING_CARD_CHECK_ICON = `<svg class="pricing-package-card-check" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M6 10.2 8.55 12.75 14.2 7.1" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function renderPricingTierCardPrice(tier: PricingTierRow, billing: PricingBillingPeriod = "monthly"): string {
  if ("priceLabel" in tier && tier.priceLabel) {
    return `<div class="pricing-package-card-price"><span class="pricing-package-card-price-value is-custom">${escapeHtml(tier.priceLabel)}</span></div>`;
  }

  const monthly = tier.priceMonthly ?? 0;
  if (monthly === 0) {
    return `<div class="pricing-package-card-price"><span class="pricing-package-card-price-value">$0</span></div>`;
  }

  const yearly = tier.priceYearly ?? 0;
  const yearlyMonthly = Math.round(yearly / 12);

  if (billing === "yearly") {
    return `<div class="pricing-package-card-price" data-tier-price="${tier.id}"><span class="pricing-package-card-price-value">$${yearlyMonthly}<span class="pricing-package-card-price-period">/mo</span></span><span class="pricing-package-card-price-yearly">$${yearly}/yr billed annually</span></div>`;
  }

  return `<div class="pricing-package-card-price" data-tier-price="${tier.id}"><span class="pricing-package-card-price-value">$${monthly}<span class="pricing-package-card-price-period">/mo</span></span></div>`;
}

function renderPricingPackageCard(tier: PricingTierCardData, billing: PricingBillingPeriod = "monthly"): string {
  const highlights = tier.cardHighlights ?? [];
  const featureItems = highlights
    .map(
      (item: string) =>
        `<li class="pricing-package-card-feature">${PRICING_CARD_CHECK_ICON}<span>${escapeHtml(item)}</span></li>`,
    )
    .join("");

  const popularBadge =
    tier.id === "core"
      ? `<span class="pricing-popular-badge">Best value</span>`
      : "";

  return `<article class="pricing-package-card" data-tier-card="${tier.id}">
    <div class="pricing-package-card-header">
      <div class="pricing-package-card-heading">
        <div class="pricing-package-card-title-row">
          <h2 class="pricing-package-card-name">${escapeHtml(tier.name)}</h2>
          ${popularBadge}
        </div>
        <p class="pricing-package-card-tagline">${escapeHtml(tier.tagline)}</p>
      </div>
      ${renderPricingTierCardPrice(tier, billing)}
    </div>
    <div class="pricing-package-card-divider" aria-hidden="true"></div>
    <div class="pricing-package-card-features">
      <p class="pricing-package-card-features-label">Features</p>
      <p class="pricing-package-card-features-intro">${escapeHtml(tier.cardFeaturesIntro ?? "")}</p>
      <ul class="pricing-package-card-feature-grid">${featureItems}</ul>
    </div>
    <div class="pricing-package-card-cta">${renderPricingTierCta(tier)}</div>
  </article>`;
}

function renderPricingBillingSwitcher(): string {
  const billing = (pricingTiersData as typeof pricingTiersData & {
    billing: { yearlyDiscountLabel: string; yearlyDiscountHint: string };
  }).billing;

  return `<div class="pricing-billing-switcher-wrap">
    <div class="pricing-billing-switcher" role="group" aria-label="Billing period">
      <button type="button" class="pricing-billing-option is-active" data-billing="monthly" aria-pressed="true">Monthly</button>
      <button type="button" class="pricing-billing-option" data-billing="yearly" aria-pressed="false">
        Yearly
        <span class="pricing-billing-save-badge">${escapeHtml(billing.yearlyDiscountLabel)}</span>
      </button>
    </div>
    <p class="pricing-billing-hint">
      <span class="pricing-billing-hint-accent">${escapeHtml(billing.yearlyDiscountLabel)}</span>
      ${escapeHtml(billing.yearlyDiscountHint)}
    </p>
  </div>`;
}

function renderPricingEnterpriseCallout(): string {
  const callout = (pricingTiersData as typeof pricingTiersData & {
    enterpriseCallout: {
      title: string;
      description: string;
      ctaLabel: string;
      ctaHref: string;
    };
  }).enterpriseCallout;

  return `<aside class="pricing-enterprise-callout" aria-labelledby="pricing-enterprise-callout-title">
    ${PRICING_ENTERPRISE_BUILDING_ICON}
    <h2 id="pricing-enterprise-callout-title" class="pricing-enterprise-callout-title">${escapeHtml(callout.title)}</h2>
    <p class="pricing-enterprise-callout-copy">${escapeHtml(callout.description)}</p>
    <a class="pricing-enterprise-callout-link" href="${escapeHtml(callout.ctaHref)}">${escapeHtml(callout.ctaLabel)}</a>
  </aside>`;
}

function getPricingCardTiers(): PricingTierCardData[] {
  return pricingTiersData.tiers
    .filter((tier) => PRICING_CARD_TIER_IDS.includes(tier.id as (typeof PRICING_CARD_TIER_IDS)[number]))
    .map((tier) => tier as PricingTierCardData);
}

function renderPricingPackagesSection(billing: PricingBillingPeriod = "monthly"): string {
  const cards = getPricingCardTiers()
    .map((tier) => renderPricingPackageCard(tier, billing))
    .join("");

  return `${renderPricingBillingSwitcher()}<div class="pricing-packages-grid">${cards}</div>${renderPricingEnterpriseCallout()}`;
}

function setupPricingBillingSwitcher(root: HTMLElement): void {
  const cardTiers = getPricingCardTiers();
  const switcher = root.querySelector<HTMLElement>(".pricing-billing-switcher-wrap");
  const grid = root.querySelector<HTMLElement>(".pricing-packages-grid");
  if (!switcher || !grid) return;

  switcher.querySelectorAll<HTMLButtonElement>(".pricing-billing-option").forEach((button) => {
    button.addEventListener("click", () => {
      const billing = button.dataset.billing === "yearly" ? "yearly" : "monthly";

      switcher.querySelectorAll<HTMLButtonElement>(".pricing-billing-option").forEach((option) => {
        const active = option === button;
        option.classList.toggle("is-active", active);
        option.setAttribute("aria-pressed", active ? "true" : "false");
      });

      cardTiers.forEach((tier) => {
        const card = grid.querySelector<HTMLElement>(`[data-tier-card="${tier.id}"]`);
        const priceMount = card?.querySelector<HTMLElement>(".pricing-package-card-header");
        const existingPrice = card?.querySelector<HTMLElement>(".pricing-package-card-price");
        if (!priceMount || !existingPrice) return;
        existingPrice.outerHTML = renderPricingTierCardPrice(tier, billing);
      });
    });
  });
}

function setupPricingPage(): void {
  const headline = document.getElementById("pricing-page-headline");
  const subheadline = document.getElementById("pricing-page-subheadline");
  const packagesMount = document.getElementById("pricing-packages-mount");
  const mount = document.getElementById("pricing-table-mount");
  if (!headline || !subheadline || !packagesMount || !mount) return;

  headline.textContent = pricingTiersData.headline;
  subheadline.textContent = pricingTiersData.subheadline;
  document.title = "Email Perfect — Pricing";

  packagesMount.innerHTML = renderPricingPackagesSection();
  setupPricingBillingSwitcher(packagesMount);

  const tierHeaders = pricingTiersData.tiers
    .map((tier) => {
      return `<th scope="col" class="pricing-tier-col"><p class="pricing-tier-name">${escapeHtml(tier.name)}</p></th>`;
    })
    .join("");

  const featureGroups = renderPricingFeatureGroups();

  const ctaCells = pricingTiersData.tiers
    .map((tier) => `<td class="pricing-table-cta-cell">${renderPricingTierCta(tier)}</td>`)
    .join("");

  mount.innerHTML = `<section class="pricing-compare-section" aria-labelledby="pricing-compare-heading">
    <h2 id="pricing-compare-heading" class="pricing-compare-heading">Compare all features</h2>
    <div class="pricing-table-wrap">
      <table class="pricing-table">
        <thead>
          <tr>
            <th scope="col" class="pricing-table-feature-col">Features</th>
            ${tierHeaders}
          </tr>
        </thead>
        ${featureGroups}
        <tfoot>
          <tr>
            <th scope="row" class="pricing-table-feature-label"></th>
            ${ctaCells}
          </tr>
        </tfoot>
      </table>
    </div>
  </section>`;

  setupPricingCompareGroups(mount);
  applyPricingGeoSeo();
}

function showPricingPage(): void {
  const marketingPage = document.getElementById("static-marketing-page");
  const pricingPage = document.getElementById("static-pricing-page");
  const toolkitPage = document.getElementById("static-toolkit-page");
  const securityPage = document.getElementById("static-security-page");
  const changelogPage = document.getElementById("static-changelog-page");
  if (marketingPage) marketingPage.hidden = true;
  if (toolkitPage) toolkitPage.hidden = true;
  if (securityPage) securityPage.hidden = true;
  if (changelogPage) changelogPage.hidden = true;
  if (pricingPage) pricingPage.hidden = false;
}

async function maybeRedirectSignedInUser(): Promise<void> {
  const path = window.location.pathname;
  if (path !== "/" && path !== "/index.html") {
    return;
  }
  try {
    const session = await fetchAuthSession();
    if (session.authenticated && session.accountId) {
      window.location.replace("/projects");
    }
  } catch {
    /* stay on marketing */
  }
}

function openLegalModal(doc: LegalDocKey): void {
  const backdrop = document.getElementById("legal-modal");
  const title = document.getElementById("legal-modal-title");
  const date = document.getElementById("legal-modal-date");
  const body = document.getElementById("legal-modal-body");
  if (!backdrop || !title || !date || !body) return;
  const d = LEGAL_DOCUMENTS[doc];
  title.textContent = d.title;
  date.textContent = d.effectiveDate;
  body.innerHTML = renderLegalBody(doc);
  wireAnchorLegalLinks(body);
  backdrop.classList.remove("hidden");
  backdrop.setAttribute("aria-hidden", "false");
}

function closeLegalModal(): void {
  const backdrop = document.getElementById("legal-modal");
  if (!backdrop) return;
  backdrop.classList.add("hidden");
  backdrop.setAttribute("aria-hidden", "true");
  const path = window.location.pathname;
  if (publicDocKeyFromPath(path)) {
    window.history.replaceState({}, "", "/");
  }
}

function clearAuthModalMessages(): void {
  for (const id of ["auth-google-error", "auth-email-error", "auth-email-success"]) {
    const el = document.getElementById(id);
    if (el) {
      el.textContent = "";
      el.classList.add("hidden");
    }
  }
}

function openAuthModal(prefillError: string | null): void {
  const backdrop = document.getElementById("auth-modal");
  if (!backdrop) return;
  clearAuthModalMessages();
  backdrop.classList.remove("hidden");
  backdrop.setAttribute("aria-hidden", "false");
  if (prefillError) {
    const err = document.getElementById("auth-google-error");
    if (err) {
      err.textContent = prefillError;
      err.classList.remove("hidden");
    }
  }
  // Pre-fill last used email
  const lastEmail = readLastMagicLinkEmail();
  if (lastEmail) {
    const input = document.getElementById("auth-email-input") as HTMLInputElement | null;
    if (input && !input.value) {
      input.value = lastEmail;
    }
  }
}

function closeAuthModal(): void {
  const backdrop = document.getElementById("auth-modal");
  if (!backdrop) return;
  backdrop.classList.add("hidden");
  backdrop.setAttribute("aria-hidden", "true");
  clearAuthModalMessages();
}

function setupLegalFromPath(): void {
  const doc = publicDocKeyFromPath(window.location.pathname);
  if (doc) openLegalModal(doc);
}

function setupHistory(): void {
  window.addEventListener("popstate", () => {
    setupLegalFromPath();
  });
}

function setupAuthUi(): void {
  const params = new URLSearchParams(window.location.search);
  let authErr: string | null = null;
  if (params.get("auth_error") === "google_profile") {
    authErr = "Unable to get profile information from Google";
  }
  if (params.get("auth_error") === "email_link_invalid") {
    authErr = "Your sign-in link is invalid or expired. Please try again.";
  }
  if (authErr) {
    openAuthModal(authErr);
  }

  const openers = document.querySelectorAll<HTMLButtonElement>("[data-open-auth]");
  openers.forEach((b) => b.addEventListener("click", () => openAuthModal(null)));

  document.getElementById("auth-modal-close")?.addEventListener("click", closeAuthModal);
  document.getElementById("auth-google-btn")?.addEventListener("click", async () => {
    const btn = document.getElementById("auth-google-btn") as HTMLButtonElement | null;
    const errEl = document.getElementById("auth-google-error");
    errEl?.classList.add("hidden");
    if (btn) btn.disabled = true;
    try {
      const { authorizationUrl } = await startGoogleAuth();
      window.location.assign(authorizationUrl);
    } catch (e) {
      console.error(e);
      if (errEl) {
        errEl.textContent = "Unable to start Google sign-in.";
        errEl.classList.remove("hidden");
      }
    } finally {
      if (btn) btn.disabled = false;
    }
  });

  const submitEmailForm = async () => {
    const input = document.getElementById("auth-email-input") as HTMLInputElement | null;
    const btn = document.getElementById("auth-email-btn") as HTMLButtonElement | null;
    const errEl = document.getElementById("auth-email-error");
    const okEl = document.getElementById("auth-email-success");
    if (!input) return;
    const email = input.value.trim();

    if (!EMAIL_PATTERN.test(email)) {
      if (errEl) {
        errEl.textContent = "Please enter a valid email address.";
        errEl.classList.remove("hidden");
      }
      okEl?.classList.add("hidden");
      return;
    }

    errEl?.classList.add("hidden");
    okEl?.classList.add("hidden");

    // Cooldown: block resend for 2 minutes to the same address
    const cooldown = readMagicLinkCooldown();
    if (
      cooldown &&
      cooldown.email === email.toLowerCase() &&
      Date.now() - cooldown.sentAt < MAGIC_LINK_COOLDOWN_MS
    ) {
      const remainingMins = Math.ceil((MAGIC_LINK_COOLDOWN_MS - (Date.now() - cooldown.sentAt)) / 60000);
      if (okEl) {
        okEl.textContent = `A sign-in link was already sent to ${email}. Check your inbox and spam folder. You can request another in ${remainingMins} minute${remainingMins !== 1 ? "s" : ""}.`;
        okEl.classList.remove("hidden");
      }
      return;
    }

    if (btn) btn.disabled = true;
    try {
      await startEmailMagicLink(email);
      writeLastMagicLinkEmail(email);
      writeMagicLinkCooldown(email);
      if (okEl) {
        okEl.textContent = `Sign-in link sent to ${email}. Check your inbox and spam folder.`;
        okEl.classList.remove("hidden");
      }
    } catch (err) {
      if (errEl) {
        errEl.innerHTML = "";
        if (err instanceof RateLimitError) {
          const tierMessages: Record<string, string> = {
            "5min":  "Too many sign-in attempts — please cool off for 5 minutes before trying again.",
            "1hr":   "Too many sign-in attempts — please cool off for an hour before trying again.",
            "1day":  "Too many sign-in attempts — please cool off for 24 hours before trying again.",
            "banned": "Your IP has been blocked due to too many sign-in attempts.",
          };
          errEl.textContent = tierMessages[err.tier] ?? "Too many attempts. Please try again later.";
          if (err.tier === "banned") {
            const contactLink = document.createElement("a");
            contactLink.textContent = " Contact support if you believe this is an error.";
            contactLink.href = "mailto:support@emailperfect.io";
            contactLink.style.cssText = "text-decoration:underline;cursor:pointer;";
            errEl.appendChild(contactLink);
          }
        } else {
          const message =
            err instanceof Error && err.message.trim()
              ? err.message.trim()
              : "Unable to send sign-in link right now. Please try again.";
          errEl.textContent = message;
        }
        errEl.classList.remove("hidden");
      }
    } finally {
      if (btn) btn.disabled = false;
    }
  };

  document.getElementById("auth-email-btn")?.addEventListener("click", () => { void submitEmailForm(); });
  document.getElementById("auth-email-input")?.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); void submitEmailForm(); }
  });
}

function setupLegalUi(): void {
  document.getElementById("legal-modal-close")?.addEventListener("click", closeLegalModal);
  const docButtons: Array<[string, LegalDocKey]> = [
    ["data-open-terms", "terms"],
    ["data-open-privacy", "privacy"],
    ["data-open-subprocessors", "subprocessors"],
  ];
  for (const [attr, doc] of docButtons) {
    document.querySelectorAll<HTMLButtonElement>(`[${attr}]`).forEach((b) =>
      b.addEventListener("click", () => {
        const path = `/${doc === "subprocessors" ? "subprocessors" : doc}`;
        window.history.pushState({}, "", path);
        openLegalModal(doc);
      }),
    );
  }
  document.querySelectorAll<HTMLButtonElement>("[data-open-security]").forEach((b) =>
    b.addEventListener("click", () => {
      window.location.assign("/security");
    }),
  );
}

function setupLandingEntranceOverlay(): void {
  const el = document.getElementById("landing-entrance-overlay");
  if (!el) {
    return;
  }
  const reveal = (): void => {
    requestAnimationFrame(() => {
      el.style.opacity = "0";
    });
    window.setTimeout(() => {
      el.remove();
    }, 500);
  };
  if (document.readyState === "complete") {
    reveal();
    return;
  }
  window.addEventListener("load", reveal, { once: true });
}

function wireAnchorLegalLinks(root: ParentNode = document): void {
  root.querySelectorAll<HTMLAnchorElement>("a[data-legal-static]").forEach((a) => {
    if (a.dataset.legalBound === "1") return;
    a.dataset.legalBound = "1";
    a.addEventListener("click", (e) => {
      const href = a.getAttribute("href") ?? "";
      let path = href;
      try {
        path = new URL(href, window.location.origin).pathname;
      } catch {
        // keep raw href
      }
      const doc = publicDocKeyFromPath(path);
      if (doc) {
        e.preventDefault();
        window.history.pushState({}, "", path);
        openLegalModal(doc);
        wireAnchorLegalLinks(document.getElementById("legal-modal-body") ?? document);
      }
    });
  });
}

// ── Invite landing flow ───────────────────────────────────────────────────────
//
// NOTE: this landing page is only ever shown for OLDER invites. Fresh invites
// (within ~24 hours of issuance) are auto-authenticated server-side at
// `GET /api/auth/invites/accept` — those users skip this page entirely and
// land directly in onboarding. By the time someone reaches here, the cautious
// magic-link verification path is the correct experience.
interface InvitePeekPayload {
  scope: "team" | "project";
  invitedEmail: string;
  inviterEmail: string;
  inviterDisplayName?: string;
  teamName: string;
  projectName: string | null;
  role: string;
  createdAtMs: number | null;
  expiresAtMs: number;
}

function formatRoleLabel(role: string): string {
  const lower = role.toLowerCase();
  switch (lower) {
    case "owner":
      return "an owner";
    case "editor":
      return "an editor";
    case "viewer":
      return "a viewer";
    case "developer":
      return "a developer";
    default:
      return `a ${lower}`;
  }
}

function resolveInviterLabel(payload: InvitePeekPayload): string {
  const name = payload.inviterDisplayName?.trim();
  if (name) return name;
  return payload.inviterEmail.trim();
}

function buildInviteHeadline(payload: InvitePeekPayload): string {
  const inviter = resolveInviterLabel(payload);
  if (payload.scope === "project" && payload.projectName) {
    return `${inviter} from ${payload.teamName} invited you to collaborate on ${payload.projectName}.`;
  }
  return `${inviter} invited you to join the team ${payload.teamName}.`;
}

function buildInviteSubmessage(payload: InvitePeekPayload): string {
  const roleLabel = formatRoleLabel(payload.role);
  return `You'll join as ${roleLabel}.`;
}

async function loadInviteSection(): Promise<void> {
  const section = document.getElementById("ep-invite-section");
  const loading = document.getElementById("ep-invite-loading");
  const box = document.getElementById("ep-invite-box");
  const expired = document.getElementById("ep-invite-expired");
  const message = document.getElementById("ep-invite-message");
  const submessage = document.getElementById("ep-invite-submessage");
  const emailInput = document.getElementById("ep-invite-email") as HTMLInputElement | null;
  const acceptBtn = document.getElementById("ep-invite-accept-btn") as HTMLButtonElement | null;
  const ctaHint = document.getElementById("ep-invite-cta-hint");
  const errorEl = document.getElementById("ep-invite-error");
  const confirmed = document.getElementById("ep-invite-confirmed");

  if (!section) return;
  section.style.display = "";

  let payload: InvitePeekPayload | null = null;
  try {
    const res = await apiFetch(`${API_BASE}/auth/invites/peek`);
    if (!res.ok) {
      if (loading) loading.style.display = "none";
      if (expired) expired.style.display = "";
      return;
    }
    payload = (await res.json()) as InvitePeekPayload;
  } catch {
    if (loading) loading.style.display = "none";
    if (expired) expired.style.display = "";
    return;
  }

  if (loading) loading.style.display = "none";

  if (message) {
    message.textContent = buildInviteHeadline(payload);
  }
  if (submessage) {
    submessage.textContent = buildInviteSubmessage(payload);
    submessage.classList.remove("hidden");
  }

  if (emailInput) {
    emailInput.value = payload.invitedEmail;
  }

  if (acceptBtn) {
    acceptBtn.textContent = "Send sign-in link";
  }
  if (ctaHint) {
    ctaHint.textContent = `We'll email a one-time sign-in link to ${payload.invitedEmail}.`;
  }

  if (box) box.style.display = "";

  acceptBtn?.addEventListener("click", async () => {
    if (!payload) return;
    if (errorEl) errorEl.classList.add("hidden");

    if (acceptBtn) acceptBtn.disabled = true;
    try {
      await startEmailMagicLink(payload.invitedEmail);
      writeMagicLinkCooldown(payload.invitedEmail);
      writeLastMagicLinkEmail(payload.invitedEmail);
      if (box) {
        acceptBtn.style.display = "none";
        if (ctaHint) ctaHint.classList.add("hidden");
        if (confirmed) confirmed.style.display = "";
      }
    } catch (err) {
      if (errorEl) {
        if (err instanceof RateLimitError) {
          errorEl.textContent = "Too many attempts. Please try again later.";
        } else {
          const message =
            err instanceof Error && err.message.trim()
              ? err.message.trim()
              : "Unable to send sign-in link right now. Please try again.";
          errorEl.textContent = message;
        }
        errorEl.classList.remove("hidden");
      }
      if (acceptBtn) acceptBtn.disabled = false;
    }
  });
}

function setupPanelImageSliceReveal(sliceId: string, panelId: string, imageId: string): void {
  const slice = document.getElementById(sliceId);
  const panel = document.getElementById(panelId);
  const image = document.getElementById(imageId);
  if (!slice || !image) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let hasRevealed = false;
  let scrollBound = false;

  const reveal = (): void => {
    if (hasRevealed) return;
    hasRevealed = true;
    image.classList.add("is-revealed");
  };

  const resetReveal = (): void => {
    hasRevealed = false;
    image.classList.remove("is-revealed");
  };

  const checkReveal = (): void => {
    if (hasRevealed) return;

    const target = panel ?? slice;
    const rect = target.getBoundingClientRect();
    const targetCenterY = rect.top + rect.height / 2;
    const triggerLineY = window.innerHeight * 0.7;
    if (targetCenterY > triggerLineY) return;

    reveal();
    unbindScroll();
  };

  const bindScroll = (): void => {
    if (scrollBound) return;
    scrollBound = true;
    window.addEventListener("scroll", checkReveal, { passive: true });
    window.addEventListener("resize", checkReveal, { passive: true });
  };

  const unbindScroll = (): void => {
    if (!scrollBound) return;
    scrollBound = false;
    window.removeEventListener("scroll", checkReveal);
    window.removeEventListener("resize", checkReveal);
  };

  const syncRevealMode = (): void => {
    unbindScroll();
    resetReveal();

    if (reduceMotion) {
      reveal();
      return;
    }

    bindScroll();
    checkReveal();
  };

  syncRevealMode();
}

const EDITOR_VIEW_TAB_HOLD_MS = 2800;
const EDITOR_VIEW_SHUTTLE_MS = 280;
const EDITOR_VIEW_REVEAL_MS = 900;

function setupEditorViewSlice(): void {
  const slice = document.getElementById("editor-view-slice");
  const panel = document.getElementById("editor-view-panel");
  const images = document.getElementById("editor-view-images");
  const track = document.getElementById("editor-view-tabs-track");
  const shuttle = document.getElementById("editor-view-tabs-shuttle");
  if (!slice || !panel || !images || !track || !shuttle) return;

  const tabButtons = [...track.querySelectorAll<HTMLButtonElement>(".editor-view-tab")];
  const captions = [...panel.querySelectorAll<HTMLElement>(".editor-view-caption")];
  const slides = [...images.querySelectorAll<HTMLImageElement>(".editor-view-slide")];
  const desktopReveal = window.matchMedia("(min-width: 1400px)");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let activeIndex = 0;
  let hasRevealed = false;
  let scrollBound = false;
  let carouselTimer: number | undefined;
  let carouselStarted = false;

  const syncShuttle = (index: number, immediate = false): void => {
    const button = tabButtons[index];
    if (!button) return;

    const left = button.offsetLeft;
    const width = button.offsetWidth;
    const height = button.offsetHeight;
    const top = button.offsetTop;

    if (immediate) {
      shuttle.style.transition = "none";
    }

    shuttle.style.left = `${left}px`;
    shuttle.style.width = `${width}px`;
    shuttle.style.height = `${height}px`;
    shuttle.style.top = `${top}px`;

    if (immediate) {
      void shuttle.offsetWidth;
      shuttle.style.transition = "";
    }
  };

  const setActiveTab = (index: number): void => {
    activeIndex = index;
    tabButtons.forEach((button, i) => {
      button.classList.toggle("is-active", i === index);
    });
    slides.forEach((slide, i) => {
      slide.classList.toggle("is-active", i === index);
    });
    captions.forEach((caption, i) => {
      caption.classList.toggle("is-active", i === index);
    });
    syncShuttle(index);
  };

  const stopCarousel = (): void => {
    if (carouselTimer !== undefined) {
      window.clearTimeout(carouselTimer);
      carouselTimer = undefined;
    }
  };

  const scheduleNextTab = (): void => {
    stopCarousel();
    carouselTimer = window.setTimeout(() => {
      const nextIndex = (activeIndex + 1) % tabButtons.length;
      setActiveTab(nextIndex);
      scheduleNextTab();
    }, EDITOR_VIEW_TAB_HOLD_MS + EDITOR_VIEW_SHUTTLE_MS);
  };

  const startCarousel = (): void => {
    if (carouselStarted || reduceMotion) return;
    carouselStarted = true;
    syncShuttle(activeIndex, true);
    scheduleNextTab();
  };

  const reveal = (): void => {
    if (hasRevealed) return;
    hasRevealed = true;
    images.classList.add("is-revealed");
    window.setTimeout(startCarousel, EDITOR_VIEW_REVEAL_MS);
  };

  const resetReveal = (): void => {
    hasRevealed = false;
    carouselStarted = false;
    stopCarousel();
    images.classList.remove("is-revealed");
    setActiveTab(0);
    syncShuttle(0, true);
  };

  const checkReveal = (): void => {
    if (hasRevealed || !desktopReveal.matches) return;

    const rect = panel.getBoundingClientRect();
    const targetCenterY = rect.top + rect.height / 2;
    const triggerLineY = window.innerHeight * 0.7;
    if (targetCenterY > triggerLineY) return;

    reveal();
    unbindScroll();
  };

  const bindScroll = (): void => {
    if (scrollBound) return;
    scrollBound = true;
    window.addEventListener("scroll", checkReveal, { passive: true });
    window.addEventListener("resize", checkReveal, { passive: true });
  };

  const unbindScroll = (): void => {
    if (!scrollBound) return;
    scrollBound = false;
    window.removeEventListener("scroll", checkReveal);
    window.removeEventListener("resize", checkReveal);
  };

  const syncLayout = (): void => {
    syncShuttle(activeIndex, true);
  };

  const syncRevealMode = (): void => {
    unbindScroll();

    if (!desktopReveal.matches) {
      resetReveal();
      syncShuttle(activeIndex, true);
      if (!reduceMotion) {
        startCarousel();
      }
      return;
    }

    resetReveal();

    if (reduceMotion) {
      reveal();
      return;
    }

    bindScroll();
    checkReveal();
  };

  tabButtons.forEach((button, index) => {
    button.addEventListener("click", () => {
      setActiveTab(index);
      if (carouselStarted) {
        scheduleNextTab();
      }
    });
  });

  syncShuttle(0, true);
  syncRevealMode();
  desktopReveal.addEventListener("change", syncRevealMode);
  window.addEventListener("resize", syncLayout, { passive: true });
}

function setupEditorViewSliceReveal(): void {
  setupEditorViewSlice();
}

function setupPropertiesViewSliceReveal(): void {
  setupPanelImageSliceReveal("properties-view-slice", "properties-view-panel", "properties-view-visual");
}

const WHY_TOOL_CROSS_MS = 1000;
const WHY_TOOL_MERGE_MS = 800;
const WHY_TOOL_MERGE_PAUSE_MS = 450;
const WHY_TOOL_REVEAL_TRIGGER_RATIO = 0.7;

function setupWhyEmailPerfectSlice(): void {
  const slice = document.getElementById("why-emailperfect-slice");
  const stage = document.getElementById("why-emailperfect-stage");
  const tools = document.getElementById("why-emailperfect-tools");
  const result = document.getElementById("why-emailperfect-result");
  if (!slice || !stage || !tools || !result) return;

  const cards = [...tools.querySelectorAll<HTMLElement>(".why-tool-card")];
  if (cards.length === 0) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const desktopAnimation = window.matchMedia("(min-width: 640px)");
  let hasStarted = false;
  let scrollBound = false;

  const prepareStrikeLines = (): void => {
    cards.forEach((card) => {
      const svg = card.querySelector<SVGSVGElement>(".why-tool-strike");
      const line = svg?.querySelector<SVGLineElement>("line");
      if (!line || !svg) return;

      let length = line.getTotalLength();
      if (length <= 0) {
        const rect = svg.getBoundingClientRect();
        length = Math.hypot(rect.width, rect.height);
      }
      if (length <= 0) return;

      line.style.setProperty("--strike-len", `${length}`);
    });
  };

  const runCrossOut = (): void => {
    prepareStrikeLines();
    void slice.offsetHeight;

    let index = 0;

    const crossNext = (): void => {
      if (index >= cards.length) {
        window.setTimeout(runMerge, WHY_TOOL_CROSS_MS + WHY_TOOL_MERGE_PAUSE_MS);
        return;
      }

      cards[index]?.classList.add("is-crossed");
      index += 1;
      window.setTimeout(crossNext, WHY_TOOL_CROSS_MS);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(crossNext);
    });
  };

  const showFinalState = (): void => {
    cards.forEach((card) => {
      card.classList.add("is-crossed");
    });
    stage.classList.add("is-complete");
    result.setAttribute("aria-hidden", "false");
  };

  const runMerge = (): void => {
    const stageRect = stage.getBoundingClientRect();
    const toolsHeight = tools.offsetHeight;
    tools.style.position = "relative";
    tools.style.height = `${toolsHeight}px`;
    tools.classList.add("is-merging");

    const startRects = cards.map((card) => {
      const rect = card.getBoundingClientRect();
      return {
        left: rect.left - stageRect.left,
        top: rect.top - stageRect.top,
        width: rect.width,
        height: rect.height,
      };
    });

    cards.forEach((card, index) => {
      const rect = startRects[index];
      if (!rect) return;
      card.style.position = "absolute";
      card.style.left = `${rect.left}px`;
      card.style.top = `${rect.top}px`;
      card.style.width = `${rect.width}px`;
      card.style.height = `${rect.height}px`;
      card.style.margin = "0";
    });

    const centerX = stageRect.width / 2;
    const centerY = toolsHeight / 2;

    requestAnimationFrame(() => {
      cards.forEach((card, index) => {
        const rect = startRects[index];
        if (!rect) return;
        const cardCenterX = rect.left + rect.width / 2;
        const cardCenterY = rect.top + rect.height / 2;
        card.style.transform = `translate(${centerX - cardCenterX}px, ${centerY - cardCenterY}px) scale(0.88)`;
      });
    });

    window.setTimeout(() => {
      stage.classList.add("is-complete");
      result.setAttribute("aria-hidden", "false");
    }, WHY_TOOL_MERGE_MS + 80);
  };

  const runSequence = (): void => {
    if (hasStarted) return;
    hasStarted = true;
    unbindScroll();

    if (!desktopAnimation.matches || reduceMotion) {
      showFinalState();
      return;
    }

    runCrossOut();
  };

  const checkReveal = (): void => {
    if (hasStarted) return;

    const rect = stage.getBoundingClientRect();
    const targetCenterY = rect.top + rect.height / 2;
    const triggerLineY = window.innerHeight * WHY_TOOL_REVEAL_TRIGGER_RATIO;
    if (targetCenterY > triggerLineY) return;

    runSequence();
  };

  const bindScroll = (): void => {
    if (scrollBound) return;
    scrollBound = true;
    window.addEventListener("scroll", checkReveal, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
  };

  const unbindScroll = (): void => {
    if (!scrollBound) return;
    scrollBound = false;
    window.removeEventListener("scroll", checkReveal);
    window.removeEventListener("resize", onResize);
  };

  const syncAnimationMode = (): void => {
    if (!desktopAnimation.matches) {
      if (!hasStarted) {
        hasStarted = true;
        unbindScroll();
      }
      showFinalState();
      return;
    }

    if (!scrollBound && !hasStarted) {
      bindScroll();
      prepareStrikeLines();
      checkReveal();
    }
  };

  const onResize = (): void => {
    if (!desktopAnimation.matches) {
      syncAnimationMode();
      return;
    }

    if (!hasStarted) prepareStrikeLines();
    checkReveal();
  };

  desktopAnimation.addEventListener("change", syncAnimationMode);

  syncAnimationMode();
}

function setupDesignControlCrawler(): void {
  const track = document.querySelector<HTMLElement>(".design-control-crawler-track");
  if (!track) return;

  const strip = track.querySelector<HTMLElement>(".design-control-crawler-set");
  if (!strip) return;

  const images = [...track.querySelectorAll("img")];
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reduceMotion) return;

  const sync = (): void => {
    const distance = Math.round(strip.offsetWidth);
    if (distance <= 0) return;

    track.style.setProperty("--crawl-distance", `${distance}px`);
    track.classList.add("is-ready");
  };

  const waitForImages = (): void => {
    void Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }
            img.addEventListener("load", () => resolve(), { once: true });
            img.addEventListener("error", () => resolve(), { once: true });
          }),
      ),
    ).then(sync);
  };

  waitForImages();
  window.addEventListener("resize", sync, { passive: true });
}

const OUTPUT_CODE_DESIGN_WIDTH = 1600;
const OUTPUT_CODE_DESIGN_HEIGHT = 800;

const OUTPUT_CODE_HTML_SCROLL_START = 1.2;
const OUTPUT_CODE_HTML_SCROLL_END = 0.7;

const OUTPUT_CODE_ICON_OPACITY_START = 0.5;
const OUTPUT_CODE_ICON_SCROLL_START = 0.8;
const OUTPUT_CODE_ICON_SCROLL_END = 0.5;
const OUTPUT_CODE_ICON_SIZE_SCALE_DESKTOP = 1;
const OUTPUT_CODE_ICON_SIZE_SCALE_MOBILE = 0.5;

const OUTPUT_CODE_ICON_OPACITY_START_MOBILE = 0.25;
const OUTPUT_CODE_ICON_SCROLL_START_MOBILE = 1.05;
const OUTPUT_CODE_ICON_SCROLL_END_MOBILE = 0.65;
const OUTPUT_CODE_ICON_Y_OFFSET_MOBILE = 180;

const OUTPUT_CODE_BLUR_START = 0.7;

function setupOutputCodeSliceScroll(): void {
  const mockup = document.getElementById("output-code-mockup");
  const frame = document.getElementById("output-code-frame");
  const stage = document.getElementById("output-code-stage");
  const html = document.getElementById("output-code-html");
  if (!mockup || !frame || !stage || !html) return;

  const icons = [...stage.querySelectorAll<HTMLImageElement>(".output-code-icon")];
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const getStageMetrics = (): { centerX: number; centerY: number; xScale: number; yScale: number } => {
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    return {
      centerX: width / 2,
      centerY: height / 2,
      xScale: width / OUTPUT_CODE_DESIGN_WIDTH,
      yScale: height / OUTPUT_CODE_DESIGN_HEIGHT,
    };
  };

  const mobileOutputCode = window.matchMedia("(max-width: 767px)");

  const getIconOpacityStart = (): number =>
    mobileOutputCode.matches ? OUTPUT_CODE_ICON_OPACITY_START_MOBILE : OUTPUT_CODE_ICON_OPACITY_START;

  const getIconOpacity = (progress: number): number => {
    const opacityStart = getIconOpacityStart();
    if (progress <= opacityStart) return 0;
    return (progress - opacityStart) / (1 - opacityStart);
  };

  const applyIconFrame = (progress: number): void => {
    const { centerX, centerY, xScale, yScale } = getStageMetrics();
    const sizeScale = mobileOutputCode.matches
      ? OUTPUT_CODE_ICON_SIZE_SCALE_MOBILE
      : OUTPUT_CODE_ICON_SIZE_SCALE_DESKTOP;
    const iconScale = (0.25 + progress * 0.25) * sizeScale;
    const opacity = getIconOpacity(progress);
    const iconYOffset = mobileOutputCode.matches ? OUTPUT_CODE_ICON_Y_OFFSET_MOBILE : 0;

    for (const icon of icons) {
      const targetX = Number(icon.dataset.x) * xScale;
      const targetY = Number(icon.dataset.y) * yScale;
      const x = centerX + (targetX - centerX) * progress;
      const y = centerY + (targetY - centerY) * progress;
      icon.style.left = `${x}px`;
      icon.style.top = `${y + iconYOffset}px`;
      icon.style.opacity = String(opacity);
      icon.style.setProperty("--output-code-icon-scale", String(iconScale));
    }
  };

  const getHtmlScale = (): number => (mobileOutputCode.matches ? 0.1875 : 0.5);

  const applyHtmlFrame = (progress: number): void => {
    const hiddenOffset = (1 - progress) * 100;
    const hiddenPx = (1 - progress) * 24;
    html.style.transform = `translate(-50%, calc(${hiddenOffset}% + ${hiddenPx}px)) scale(${getHtmlScale()})`;
  };

  const getScrollProgress = (start: number, end: number): number => {
    const rect = mockup.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const mockupCenterY = rect.top + rect.height / 2;
    const startLine = viewportHeight * start;
    const endLine = viewportHeight * end;
    return Math.min(1, Math.max(0, (startLine - mockupCenterY) / (startLine - endLine)));
  };

  const getHtmlProgress = (): number =>
    getScrollProgress(OUTPUT_CODE_HTML_SCROLL_START, OUTPUT_CODE_HTML_SCROLL_END);

  const getIconProgress = (): number => {
    const start = mobileOutputCode.matches
      ? OUTPUT_CODE_ICON_SCROLL_START_MOBILE
      : OUTPUT_CODE_ICON_SCROLL_START;
    const end = mobileOutputCode.matches ? OUTPUT_CODE_ICON_SCROLL_END_MOBILE : OUTPUT_CODE_ICON_SCROLL_END;
    return getScrollProgress(start, end);
  };

  const getMockupBlurProgress = (htmlProgress: number): number => {
    if (htmlProgress <= OUTPUT_CODE_BLUR_START) return 0;
    return (htmlProgress - OUTPUT_CODE_BLUR_START) / (1 - OUTPUT_CODE_BLUR_START);
  };

  const applyMockupBlur = (htmlProgress: number): void => {
    const blurProgress = getMockupBlurProgress(htmlProgress);
    const blurPx = Math.min(12, blurProgress * 12);
    mockup.style.filter = blurPx > 0 ? `blur(${blurPx}px)` : "none";
  };

  const update = (): void => {
    if (reduceMotion) {
      applyIconFrame(1);
      applyHtmlFrame(1);
      applyMockupBlur(1);
      return;
    }

    const htmlProgress = getHtmlProgress();
    const iconProgress = getIconProgress();
    applyIconFrame(iconProgress);
    applyHtmlFrame(htmlProgress);
    applyMockupBlur(htmlProgress);
  };

  window.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update, { passive: true });
  update();
}

function setupMarketingHeaderMenu(): void {
  const shell = document.getElementById("marketing-header-shell");
  const toggle = document.getElementById("marketing-header-toggle");
  const nav = document.getElementById("marketing-header-nav");
  if (!shell || !toggle || !nav) return;

  const desktopNav = window.matchMedia("(min-width: 768px)");

  const setOpen = (open: boolean): void => {
    shell.classList.toggle("is-open", open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
  };

  toggle.addEventListener("click", () => {
    setOpen(!shell.classList.contains("is-open"));
  });

  nav.querySelectorAll("a, button").forEach((item) => {
    item.addEventListener("click", () => {
      if (!desktopNav.matches) {
        setOpen(false);
      }
    });
  });

  desktopNav.addEventListener("change", () => {
    if (desktopNav.matches) {
      setOpen(false);
    }
  });
}

// ─────────────────────────────────────────────────────────────────────────────

setupLandingEntranceOverlay();
redirectLegacyLegalPaths();

// If the user arrived via an invite link and has no session, show the invite
// landing page instead of the marketing page.
if (new URLSearchParams(window.location.search).get("invite_pending") === "1") {
  const marketingPage = document.getElementById("static-marketing-page");
  const pricingPage = document.getElementById("static-pricing-page");
  const toolkitPage = document.getElementById("static-toolkit-page");
  const securityPage = document.getElementById("static-security-page");
  const changelogPage = document.getElementById("static-changelog-page");
  if (marketingPage) marketingPage.style.display = "none";
  if (pricingPage) pricingPage.hidden = true;
  if (toolkitPage) toolkitPage.hidden = true;
  if (securityPage) securityPage.hidden = true;
  if (changelogPage) changelogPage.hidden = true;
  void loadInviteSection();
} else if (isSecurityPath()) {
  redirectLegacyLegalPaths();
  showSecurityPage();
  setupSecurityPage(wireAnchorLegalLinks);
  setupMarketingHeaderMenu();
  setupAuthUi();
  setupLegalUi();
  setupLegalFromPath();
  setupHistory();
  wireAnchorLegalLinks();
} else if (isToolkitPath()) {
  showToolkitPage();
  setupToolkitPage();
  setupMarketingHeaderMenu();
  setupAuthUi();
  setupLegalUi();
  setupLegalFromPath();
  setupHistory();
  wireAnchorLegalLinks();
} else if (isChangelogPath()) {
  showChangelogPage();
  setupChangelogPage();
  setupMarketingHeaderMenu();
  setupAuthUi();
  setupLegalUi();
  setupLegalFromPath();
  setupHistory();
  wireAnchorLegalLinks();
} else if (isPricingPath()) {
  showPricingPage();
  setupPricingPage();
  injectFaq("pricing-faq-col-left", "pricing-faq-col-right");
  setupMarketingHeaderMenu();
  setupAuthUi();
  setupLegalUi();
  setupLegalFromPath();
  setupHistory();
  wireAnchorLegalLinks();
  void maybeRedirectSignedInUser();
} else {
  injectFaq();
  applyHomeGeoSeo();
  setupEditorViewSliceReveal();
  setupPropertiesViewSliceReveal();
  setupWhyEmailPerfectSlice();
  setupDesignControlCrawler();
  setupOutputCodeSliceScroll();
  setupMarketingHeaderMenu();
  setupAuthUi();
  setupLegalUi();
  setupLegalFromPath();
  setupHistory();
  wireAnchorLegalLinks();
  void maybeRedirectSignedInUser();
}
